import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  ListSubheader,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Account } from '@/api/accounts';
import { ErrorCodes, isApiError } from '@/api/errors';
import {
  plannedApi,
  type ExceptionPreview,
  type Fulfilment,
  type RecurrenceRule,
  type Series,
  type SeriesPatch,
  type SliceMode,
  type TargetRequest,
} from '@/api/planned';
import { applyBackendFields, validationAlert } from '@/features/shared/useFieldErrors';
import { bpToInputText, minorToInputText, parseMoneyToMinor, percentToBp } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { toCivil } from '@/features/envelope/window';
import { isOwnerAccount } from '@/features/accounts/accountAccess';
import { useOwnedEnvelopes } from '@/features/accounts/useOwnedEnvelopes';
import { currencyRefusedRows } from '@/features/accounts/allocationErrors';
import { EXCEPTIONS_TITLE_ID, ExceptionsPanel } from './ExceptionsPanel';
import { plannedErrorKey } from './plannedErrors';
import { RECURRENCE_TITLE_ID, RecurrencePanel } from './recurrence/RecurrencePanel';
import { describeRecurrence, emptyRule, validateRule } from './recurrence/recurrence';
import { SubmitButton } from '@/components/SubmitButton';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubscribeInPlace } from '@/features/subscribe/SubscribeInPlace';
import { useReturnFocus } from '@/components/useConfirm';
import { ErrorSummary, type ErrorSummaryItem } from '@/components/ErrorSummary';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { sameValues, useDirty } from '@/shared/useDirty';

const TITLE_ID = 'series-editor-title';
const SUBSCRIBE_TITLE_ID = 'series-editor-subscribe-title';
const SAVE_OPENER = 'series-save';
const RECURRENCE_OPENER = 'series-recurrence';
const NONE = '';

/** YAML PlannedSeriesCreateRequest / PatchRequest names, plus spec `plan[i].*` / `matching.*`. */
const SERIES_TOP_FIELDS = [
  'name',
  'kind',
  'accountId',
  'amountMinor',
  'startOn',
  'endOn',
  'endsOn',
  'fulfilment',
  'payee',
] as const;
const RECURRENCE_FIELDS = [
  'recurrence',
  'recurrence.frequency',
  'recurrence.startOn',
  'recurrence.endsOn',
  'recurrence.endOn',
  'recurrence.interval',
  'recurrence.occurrenceCount',
  'recurrence.byMonth',
  'recurrence.byMonthDays',
  'recurrence.byOrdinalWeekday',
  'recurrence.byWeekOrdinal',
  'recurrence.byWeekdays',
] as const;
const MATCH_FIELDS = [
  'match',
  'matching',
  'match.payeeContains',
  'match.amountToleranceBp',
  'match.windowDays',
  'matching.payeeContains',
  'matching.amountToleranceBp',
  'matching.windowDays',
] as const;
const PLAN_PROPS = [
  'envelopeId',
  'amountMinor',
  'percentBp',
  'categoryId',
  'fixedAmountMinor',
] as const;

type SeriesField =
  | 'name'
  | 'kind'
  | 'accountId'
  | 'amountMinor'
  | 'payee'
  | 'fulfilment'
  | 'recurrence'
  | 'payeeContains'
  | 'amountToleranceBp'
  | 'windowDays';
type PlanField = 'envelopeId' | 'amountMinor' | 'percentBp' | 'categoryId';

function seriesKnownFields(planCount: number): string[] {
  const indexed: string[] = [];
  for (let i = 0; i < planCount; i += 1) {
    for (const prop of PLAN_PROPS) {
      indexed.push(`plan[${i}].${prop}`, `targets[${i}].${prop}`);
    }
  }
  return [...SERIES_TOP_FIELDS, ...RECURRENCE_FIELDS, ...MATCH_FIELDS, ...indexed];
}

function planProp(prop: string): PlanField | null {
  if (prop === 'envelopeId' || prop === 'percentBp' || prop === 'categoryId') return prop;
  if (prop === 'amountMinor' || prop === 'fixedAmountMinor') return 'amountMinor';
  return null;
}

interface TargetRow {
  key: string;
  envelopeId: number | '';
  mode: SliceMode;
  amount: string;
  percent: string;
  categoryId: number | '';
  seriesTargetId?: number;
}

export type EditorMode = 'create' | 'series' | 'following';

interface SeriesEditorDialogProps {
  mode: EditorMode;
  accounts: Account[];
  series?: Series | undefined;
  fromDate?: string | undefined;
  startOn?: string | undefined;
  lockedAccountId?: number | undefined;
  onClose: () => void;
  onSaved: (message: string) => void;
}

/** Editable amount / percent text is in the locale's separators — the parsers read it back. */
function rowFromTarget(
  t: Series['targets'][number],
  currency: string | null | undefined,
  locale: string,
): TargetRow {
  return {
    key: String(t.id),
    envelopeId: t.envelopeId,
    mode: t.mode,
    amount:
      t.fixedAmountMinor !== null
        ? minorToInputText(Math.abs(t.fixedAmountMinor), currency, locale)
        : '',
    percent: t.percentBp !== null ? bpToInputText(t.percentBp, locale) : '',
    categoryId: t.categoryId ?? '',
    seriesTargetId: t.id,
  };
}

/**
 * Create / edit-whole / edit-this-and-following series editor (TMMAPP-0009).
 */
export function SeriesEditorDialog({
  mode,
  accounts,
  series,
  fromDate,
  startOn,
  lockedAccountId,
  onClose,
  onSaved,
}: SeriesEditorDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const { envelopes } = useOwnedEnvelopes();
  const [accountId, setAccountId] = useState(
    lockedAccountId ?? series?.accountId ?? accounts[0]?.id ?? 0,
  );
  const seedAccount = accounts.find((a) => a.id === accountId);
  const seedCurrency = seedAccount?.currency ?? seedAccount?.operatingCurrency;
  const [name, setName] = useState(series?.name ?? '');
  const [payee, setPayee] = useState(series?.payee ?? '');
  const [kind, setKind] = useState<'INCOME' | 'EXPENSE'>(series?.kind ?? 'EXPENSE');
  const [amount, setAmount] = useState(
    series !== undefined
      ? minorToInputText(Math.abs(series.amountMinor), seedCurrency, locale)
      : '',
  );
  const [rule, setRule] = useState<RecurrenceRule>(
    series?.recurrence ?? emptyRule(startOn ?? toCivil(new Date())),
  );
  // The recurrence form shows in place of this form, in this same dialog (DLG-005), while it holds
  // a draft; this form's own values are back, as they were, when it closes.
  const [recurrenceDraft, setRecurrenceDraft] = useState<RecurrenceRule | null>(null);
  const [fulfilment, setFulfilment] = useState<Fulfilment>(series?.fulfilment ?? 'EXPECTED');
  const [payeeContains, setPayeeContains] = useState(series?.match.payeeContains ?? '');
  const [tolerance, setTolerance] = useState(
    series?.match.amountToleranceBp != null
      ? bpToInputText(series.match.amountToleranceBp, locale)
      : '',
  );
  const [windowDays, setWindowDays] = useState(
    series?.match.windowDays != null ? String(series.match.windowDays) : '',
  );
  const [advanced, setAdvanced] = useState(false);
  const [targets, setTargets] = useState<TargetRow[]>(
    series !== undefined && series.targets.length > 0
      ? series.targets.map((target) => rowFromTarget(target, seedCurrency, locale))
      : [
          {
            key: 'n1',
            envelopeId: '',
            mode: 'FIXED',
            amount: '',
            percent: '',
            categoryId: '',
          },
        ],
  );
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<SeriesField, string>>>({});
  const [rowFieldErrors, setRowFieldErrors] = useState<
    Record<string, Partial<Record<PlanField, string>>>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState<ExceptionPreview[] | null>(null);
  // SUBSCRIPTION_REQUIRED shows the subscribe card in place of the form (TMMAPP-0097 §C 25).
  const [subscribeRequired, setSubscribeRequired] = useState(false);
  // The step closed because its save failed: the error owns focus then, not the Save that opened it.
  const [stepFailed, setStepFailed] = useState(false);
  const { dirty } = useDirty({
    accountId,
    name,
    payee,
    kind,
    amount,
    rule,
    fulfilment,
    payeeContains,
    tolerance,
    windowDays,
    targets,
  });
  const guard = useDialogDirtyGuard(dirty, onClose, submitting);
  const recurrenceGuard = useDialogDirtyGuard(
    recurrenceDraft !== null && !sameValues(recurrenceDraft, rule),
    () => setRecurrenceDraft(null),
    false,
    0,
    recurrenceDraft !== null,
  );
  const account = accounts.find((a) => a.id === accountId);
  const owner = account !== undefined && isOwnerAccount(account);
  const currency = account?.currency ?? account?.operatingCurrency;
  const mag = parseMoneyToMinor(amount, currency, locale);
  const signed = mag === null ? null : kind === 'EXPENSE' ? -Math.abs(mag) : Math.abs(mag);
  const remainderCount = targets.filter((r) => r.mode === 'REMAINDER').length;
  const planOver = useMemo(() => {
    if (mag === null || mag === 0) return false;
    let used = 0;
    for (const row of targets) {
      if (row.mode === 'FIXED') {
        const n = parseMoneyToMinor(row.amount, currency, locale);
        if (n !== null) used += Math.abs(n);
      } else if (row.mode === 'PERCENT') {
        const bp = percentToBp(row.percent, locale);
        if (bp !== null) used += Math.round((Math.abs(mag) * bp) / 10000);
      }
    }
    return used > Math.abs(mag);
  }, [targets, mag, currency, locale]);

  const groups = useMemo(() => {
    const g: { name: string; envelopes: typeof envelopes }[] = [];
    for (const envelope of envelopes) {
      const existing = g.find((x) => x.name === envelope.virtualAccountName);
      if (existing) existing.envelopes.push(envelope);
      else g.push({ name: envelope.virtualAccountName, envelopes: [envelope] });
    }
    return g;
  }, [envelopes]);

  function collectSubmission(): { requests: TargetRequest[]; rows: TargetRow[] } {
    const sign = kind === 'EXPENSE' ? -1 : 1;
    const requests: TargetRequest[] = [];
    const rows: TargetRow[] = [];
    for (const row of targets) {
      if (typeof row.envelopeId !== 'number') continue;
      const req: TargetRequest = {
        envelopeId: row.envelopeId,
        mode: row.mode,
      };
      if (row.seriesTargetId !== undefined) req.seriesTargetId = row.seriesTargetId;
      if (row.mode === 'FIXED') {
        const n = parseMoneyToMinor(row.amount, currency, locale);
        if (n === null) continue;
        req.fixedAmountMinor = sign * Math.abs(n);
      }
      if (row.mode === 'PERCENT') {
        const bp = percentToBp(row.percent, locale);
        if (bp === null) continue;
        req.percentBp = bp;
      }
      if (typeof row.categoryId === 'number') req.categoryId = row.categoryId;
      requests.push(req);
      rows.push(row);
    }
    return { requests, rows };
  }

  function collectTargets(): TargetRequest[] {
    return collectSubmission().requests;
  }

  function applySeriesValidation(err: unknown) {
    const submitted = collectSubmission();
    const next: Partial<Record<SeriesField, string>> = {};
    const rowNext: Record<string, Partial<Record<PlanField, string>>> = {};
    const result = applyBackendFields(
      err,
      seriesKnownFields(submitted.rows.length),
      (field, message) => {
        if (
          field === 'recurrence' ||
          field.startsWith('recurrence.') ||
          field === 'startOn' ||
          field === 'endOn' ||
          field === 'endsOn'
        ) {
          next.recurrence = message;
          return;
        }
        const matchField =
          /^(?:match|matching)\.(payeeContains|amountToleranceBp|windowDays)$/.exec(field);
        if (matchField !== null || field === 'match' || field === 'matching') {
          const key = (matchField?.[1] ?? 'payeeContains') as
            'payeeContains' | 'amountToleranceBp' | 'windowDays';
          next[key] = message;
          setAdvanced(true);
          return;
        }
        const planMatch = /^(?:plan|targets)\[(\d+)\]\.(.+)$/.exec(field);
        if (planMatch !== null) {
          const row = submitted.rows[Number(planMatch[1])];
          const prop = planProp(planMatch[2] ?? '');
          if (row === undefined || prop === null) return;
          const slot = rowNext[row.key] ?? {};
          slot[prop] = message;
          rowNext[row.key] = slot;
          return;
        }
        if (
          field === 'name' ||
          field === 'kind' ||
          field === 'accountId' ||
          field === 'amountMinor' ||
          field === 'payee' ||
          field === 'fulfilment'
        ) {
          next[field] = message;
        }
      },
      t,
      guard.focusErrors,
    );
    setFieldErrors(next);
    setRowFieldErrors(rowNext);
    setError(validationAlert(result, err, t, t('planned.errorInvalid')));
  }

  /** Every field in error, in the form's visual order — the summary a failed save focuses. */
  function summaryItems(): ErrorSummaryItem[] {
    const items: ErrorSummaryItem[] = [];
    const top: [SeriesField, string, string][] = [
      ['accountId', 'series-account', t('planned.account')],
      ['name', 'series-name', t('planned.name')],
      ['payee', 'series-payee', t('planned.payee')],
      ['kind', 'series-kind', t('planned.kindHelp')],
      ['amountMinor', 'series-amount', t('planned.amount')],
      ['recurrence', 'series-recurrence', t('planned.editRecurrence')],
    ];
    for (const [field, id, label] of top) {
      const message = fieldErrors[field];
      if (message !== undefined) items.push({ id, label, message });
    }
    for (const row of targets) {
      const errs = rowFieldErrors[row.key] ?? {};
      const plan: [PlanField, string, string][] = [
        ['envelopeId', 'envelope', t('planned.pickEnvelope')],
        ['amountMinor', 'amount', t('planned.amount')],
        ['percentBp', 'percent', t('planned.percent')],
        ['categoryId', 'cat', t('accounts.colCategory')],
      ];
      for (const [field, suffix, label] of plan) {
        const message = errs[field];
        if (message !== undefined) {
          items.push({ id: `series-t-${row.key}-${suffix}`, label, message });
        }
      }
    }
    const rest: [SeriesField, string, string][] = [
      ['fulfilment', 'series-fulfilment', t('planned.expected')],
      ['payeeContains', 'series-payee-contains', t('planned.payeeContains')],
      ['amountToleranceBp', 'series-tolerance', t('planned.amountTolerance')],
      ['windowDays', 'series-window', t('planned.windowDays')],
    ];
    for (const [field, id, label] of rest) {
      const message = fieldErrors[field];
      if (message !== undefined) items.push({ id, label, message });
    }
    return items;
  }

  function patchBody(): SeriesPatch {
    const body: SeriesPatch = {
      name: name.trim(),
      kind,
      amountMinor: signed ?? 0,
      recurrence: rule,
      fulfilment,
    };
    if (payee.trim().length > 0) body.payee = payee.trim();
    const match = {
      payeeContains: payeeContains.trim().length > 0 ? payeeContains.trim() : null,
      amountToleranceBp: tolerance.trim().length > 0 ? percentToBp(tolerance, locale) : null,
      windowDays:
        windowDays.trim().length > 0 && Number.isFinite(Number(windowDays))
          ? Number(windowDays)
          : null,
    };
    body.match = match;
    body.targets = collectTargets();
    return body;
  }

  async function commit(exceptions?: 'KEEP' | 'RESET') {
    if (signed === null || signed === 0 || name.trim().length === 0) return;
    if (validateRule(rule) !== null || planOver || remainderCount > 1 || submitting) return;
    setSubmitting(true);
    setError(null);
    setFieldErrors({});
    setRowFieldErrors({});
    try {
      if (mode === 'create') {
        const patch = patchBody();
        await plannedApi.createSeries({
          accountId,
          name: name.trim(),
          kind,
          amountMinor: signed,
          recurrence: rule,
          ...(patch.payee !== undefined ? { payee: patch.payee } : {}),
          ...(patch.fulfilment !== undefined ? { fulfilment: patch.fulfilment } : {}),
          ...(patch.match !== undefined ? { match: patch.match } : {}),
          ...(patch.targets !== undefined ? { targets: patch.targets } : {}),
        });
        onSaved(t('planned.seriesCreated'));
      } else if (mode === 'following' && series !== undefined && fromDate !== undefined) {
        const saved = await plannedApi.split(series.id, {
          fromDate,
          changes: patchBody(),
          ...(exceptions !== undefined ? { exceptions } : {}),
        });
        const dropped = saved.exceptionsDropped?.length ?? 0;
        onSaved(
          dropped > 0
            ? t('planned.exceptionsDropped', { count: dropped })
            : t('planned.seriesSplit'),
        );
      } else if (series !== undefined) {
        const saved = await plannedApi.patchSeries(series.id, {
          ...patchBody(),
          applyTo: 'SERIES',
          ...(exceptions !== undefined ? { exceptions } : {}),
        });
        const dropped = saved.exceptionsDropped?.length ?? 0;
        onSaved(
          dropped > 0
            ? t('planned.exceptionsDropped', { count: dropped })
            : t('planned.seriesSaved'),
        );
      }
      setPreview(null);
      onClose();
    } catch (err) {
      // A failed save closes the exceptions step: the error shows in the editor behind it, and takes
      // focus (`focusErrors`), which Save must not take back.
      setStepFailed(true);
      setPreview(null);
      if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        setSubscribeRequired(true);
      } else if (isApiError(err) && err.code === ErrorCodes.VALIDATION_FAILED) {
        applySeriesValidation(err);
      } else {
        // A part into a virtual account in another currency (TMMAPI-0205): the parts bound there
        // are marked; the dialog-level sentence stays as the summary and the fallback.
        const refused = currencyRefusedRows(
          err,
          targets.map((row) => ({
            key: row.key,
            virtualAccountId: envelopes.find((v) => v.id === row.envelopeId)?.virtualAccountId,
          })),
        );
        if (refused.length > 0) {
          const message = t('allocations.errorCurrencyMismatch');
          setRowFieldErrors(
            Object.fromEntries(refused.map((key) => [key, { envelopeId: message }])),
          );
        }
        setError(t(plannedErrorKey(err) ?? 'planned.errorGeneric'));
        guard.focusErrors();
      }
      setSubmitting(false);
    }
  }

  async function handleSubmit() {
    if (mode === 'create') {
      await commit();
      return;
    }
    if (series === undefined) return;
    setSubmitting(true);
    setError(null);
    setFieldErrors({});
    setRowFieldErrors({});
    try {
      const rows = await plannedApi.exceptions(series.id, rule);
      const filtered =
        mode === 'following' && fromDate !== undefined
          ? rows.filter((r) => r.originalOn >= fromDate)
          : rows;
      setSubmitting(false);
      if (filtered.length === 0) {
        await commit('KEEP');
        return;
      }
      setStepFailed(false);
      setPreview(filtered);
    } catch (err) {
      if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        setSubscribeRequired(true);
      } else if (isApiError(err) && err.code === ErrorCodes.VALIDATION_FAILED) {
        applySeriesValidation(err);
      } else {
        setError(t(plannedErrorKey(err) ?? 'planned.errorGeneric'));
        guard.focusErrors();
      }
      setSubmitting(false);
    }
  }

  const summary = summaryItems();
  const title =
    mode === 'create'
      ? t('planned.newSeriesTitle')
      : mode === 'following'
        ? t('planned.editFollowing')
        : t('planned.editSeriesTitle');

  // Dismissing the exceptions step (Cancel, Esc) returns focus to the Save that opened it (DLG-004);
  // a failed save does not (the error is focused instead).
  useReturnFocus(preview !== null, stepFailed ? null : SAVE_OPENER);
  // Closing the recurrence form returns focus to the button that opened it (DLG-004).
  useReturnFocus(recurrenceDraft !== null, RECURRENCE_OPENER);
  // Back from the subscribe card, focus returns to the Save that asked (TMMAPP-0097 §C 25).
  useReturnFocus(subscribeRequired, SAVE_OPENER);

  // The Keep / Reset exceptions step shows in place of the form, in this same dialog — dialogs never
  // stack (DLG-005). Both returns are the same `GuardedDialog`, so the one `Dialog` stays mounted
  // across the swap; the form's values live in this component's state and are back when it closes.
  // The subscribe card in place of the form, in this same dialog. Esc and Cancel go back to the form.
  if (subscribeRequired) {
    return (
      <GuardedDialog
        guard={guard}
        confirm={{
          titleId: SUBSCRIBE_TITLE_ID,
          onClose: () => setSubscribeRequired(false),
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogContent>
          <SubscribeInPlace titleId={SUBSCRIBE_TITLE_ID} focusTitle onClose={onClose} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscribeRequired(false)}>{t('common.cancel')}</Button>
        </DialogActions>
      </GuardedDialog>
    );
  }

  if (preview !== null) {
    return (
      <GuardedDialog
        guard={guard}
        confirm={{
          titleId: EXCEPTIONS_TITLE_ID,
          onClose: submitting ? undefined : () => setPreview(null),
        }}
        fullWidth
        maxWidth="md"
      >
        <ExceptionsPanel
          rows={preview}
          busy={submitting}
          onClose={() => setPreview(null)}
          onChoose={(policy) => void commit(policy)}
        />
      </GuardedDialog>
    );
  }

  if (recurrenceDraft !== null) {
    return (
      // A held navigation asks the editor's own question here, in this same dialog: it outranks the
      // recurrence draft's guard, as it outranks the exceptions step (TMM-B5).
      <GuardedDialog
        guard={guard.discarding ? guard : recurrenceGuard}
        titleId={RECURRENCE_TITLE_ID}
        fullWidth
        maxWidth="md"
      >
        <RecurrencePanel
          draft={recurrenceDraft}
          setDraft={(next) =>
            setRecurrenceDraft((prev) =>
              prev === null ? null : typeof next === 'function' ? next(prev) : next,
            )
          }
          mode="series"
          focusFirst
          onCancel={recurrenceGuard.requestClose}
          onApply={() => {
            setRule(recurrenceDraft);
            setRecurrenceDraft(null);
          }}
        />
      </GuardedDialog>
    );
  }

  return (
    <>
      <GuardedDialog guard={guard} titleId={TITLE_ID} fullWidth maxWidth="md">
        <DialogTitle id={TITLE_ID}>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {summary.length > 0 ? (
              <ErrorSummary items={summary} message={error} />
            ) : (
              error !== null && <Alert severity="error">{error}</Alert>
            )}
            <FormControl
              fullWidth
              disabled={mode !== 'create'}
              error={fieldErrors.accountId !== undefined}
            >
              <InputLabel id="series-account">{t('planned.account')}</InputLabel>
              <Select
                labelId="series-account"
                SelectDisplayProps={{ id: 'series-account' }}
                label={t('planned.account')}
                value={accountId}
                onChange={(event) => setAccountId(Number(event.target.value))}
              >
                {accounts.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.accountId !== undefined && (
                <FormHelperText>{fieldErrors.accountId}</FormHelperText>
              )}
            </FormControl>
            <TextField
              id="series-name"
              label={t('planned.name')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              fullWidth
              error={fieldErrors.name !== undefined}
              helperText={fieldErrors.name}
            />
            <TextField
              id="series-payee"
              label={t('planned.payee')}
              value={payee}
              onChange={(event) => setPayee(event.target.value)}
              fullWidth
              error={fieldErrors.payee !== undefined}
              helperText={fieldErrors.payee}
            />
            <FormControl error={fieldErrors.kind !== undefined}>
              <ToggleButtonGroup
                id="series-kind"
                exclusive
                value={kind}
                onChange={(_event, value: 'INCOME' | 'EXPENSE' | null) => {
                  if (value !== null) setKind(value);
                }}
                aria-label={t('planned.kindHelp')}
              >
                <ToggleButton value="INCOME">{t('planned.kind.INCOME')}</ToggleButton>
                <ToggleButton value="EXPENSE">{t('planned.kind.EXPENSE')}</ToggleButton>
              </ToggleButtonGroup>
              <FormHelperText>{fieldErrors.kind ?? t('planned.kindHelp')}</FormHelperText>
            </FormControl>
            <TextField
              id="series-amount"
              label={t('planned.amount')}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              required
              fullWidth
              error={fieldErrors.amountMinor !== undefined}
              helperText={fieldErrors.amountMinor}
            />
            <Stack>
              <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2">{describeRecurrence(rule, locale, t)}</Typography>
                <Button
                  id="series-recurrence"
                  data-confirm-opener={RECURRENCE_OPENER}
                  onClick={() => setRecurrenceDraft(rule)}
                >
                  {t('planned.editRecurrence')}
                </Button>
              </Stack>
              {fieldErrors.recurrence !== undefined && (
                <FormHelperText error>{fieldErrors.recurrence}</FormHelperText>
              )}
            </Stack>
            <Typography variant="subtitle2" component="h3">
              {t('planned.targets')}
            </Typography>
            {planOver && <Alert severity="error">{t('planned.planExceeds')}</Alert>}
            {remainderCount > 1 && <Alert severity="error">{t('planned.remainderHelp')}</Alert>}
            {targets.map((row) => {
              const envelope = envelopes.find((v) => v.id === row.envelopeId);
              const cats = (envelope?.categories ?? []).filter(
                (c) => c.archivedAt === null && c.kind === kind,
              );
              const rowErrs = rowFieldErrors[row.key] ?? {};
              return (
                <Stack key={row.key} direction={{ xs: 'column', md: 'row' }} sx={{ gap: 1 }}>
                  <FormControl
                    size="small"
                    sx={{ minWidth: 200, flex: 1 }}
                    error={rowErrs.envelopeId !== undefined}
                  >
                    <InputLabel id={`t-envelope-${row.key}`}>
                      {t('planned.pickEnvelope')}
                    </InputLabel>
                    <Select
                      labelId={`t-envelope-${row.key}`}
                      SelectDisplayProps={{ id: `series-t-${row.key}-envelope` }}
                      label={t('planned.pickEnvelope')}
                      value={row.envelopeId === '' ? NONE : String(row.envelopeId)}
                      onChange={(event) => {
                        const v = event.target.value;
                        setTargets((prev) =>
                          prev.map((r) =>
                            r.key === row.key
                              ? {
                                  ...r,
                                  envelopeId: v === NONE ? '' : Number(v),
                                  categoryId: '',
                                }
                              : r,
                          ),
                        );
                      }}
                    >
                      <MenuItem value={NONE}>{t('planned.pickEnvelope')}</MenuItem>
                      {groups.flatMap((g) => [
                        <ListSubheader key={`h-${g.name}`}>{g.name}</ListSubheader>,
                        ...g.envelopes.map((v) => (
                          <MenuItem key={v.id} value={String(v.id)}>
                            {v.name}
                          </MenuItem>
                        )),
                      ])}
                    </Select>
                    {rowErrs.envelopeId !== undefined && (
                      <FormHelperText>{rowErrs.envelopeId}</FormHelperText>
                    )}
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id={`t-mode-${row.key}`}>{t('planned.mode.FIXED')}</InputLabel>
                    <Select
                      labelId={`t-mode-${row.key}`}
                      label={t('planned.mode.FIXED')}
                      value={row.mode}
                      onChange={(event) => {
                        const next = event.target.value as SliceMode;
                        setTargets((prev) =>
                          prev.map((r) => (r.key === row.key ? { ...r, mode: next } : r)),
                        );
                      }}
                    >
                      <MenuItem value="FIXED">{t('planned.mode.FIXED')}</MenuItem>
                      <MenuItem value="PERCENT">{t('planned.mode.PERCENT')}</MenuItem>
                      <MenuItem value="REMAINDER">{t('planned.mode.REMAINDER')}</MenuItem>
                    </Select>
                  </FormControl>
                  {row.mode === 'FIXED' && (
                    <TextField
                      id={`series-t-${row.key}-amount`}
                      size="small"
                      label={t('planned.amount')}
                      value={row.amount}
                      onChange={(event) =>
                        setTargets((prev) =>
                          prev.map((r) =>
                            r.key === row.key ? { ...r, amount: event.target.value } : r,
                          ),
                        )
                      }
                      error={rowErrs.amountMinor !== undefined}
                      helperText={rowErrs.amountMinor}
                    />
                  )}
                  {row.mode === 'PERCENT' && (
                    <TextField
                      id={`series-t-${row.key}-percent`}
                      size="small"
                      label={t('planned.percent')}
                      value={row.percent}
                      onChange={(event) =>
                        setTargets((prev) =>
                          prev.map((r) =>
                            r.key === row.key ? { ...r, percent: event.target.value } : r,
                          ),
                        )
                      }
                      error={rowErrs.percentBp !== undefined}
                      helperText={rowErrs.percentBp}
                    />
                  )}
                  <FormControl
                    size="small"
                    sx={{ minWidth: 140 }}
                    disabled={envelope == null}
                    error={rowErrs.categoryId !== undefined}
                  >
                    <InputLabel id={`t-cat-${row.key}`}>{t('accounts.colCategory')}</InputLabel>
                    <Select
                      labelId={`t-cat-${row.key}`}
                      SelectDisplayProps={{ id: `series-t-${row.key}-cat` }}
                      label={t('accounts.colCategory')}
                      value={row.categoryId === '' ? NONE : String(row.categoryId)}
                      onChange={(event) => {
                        const v = event.target.value;
                        setTargets((prev) =>
                          prev.map((r) =>
                            r.key === row.key
                              ? { ...r, categoryId: v === NONE ? '' : Number(v) }
                              : r,
                          ),
                        );
                      }}
                    >
                      <MenuItem value={NONE}>{t('envelope.uncategorized')}</MenuItem>
                      {cats.map((c) => (
                        <MenuItem key={c.id} value={String(c.id)}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {rowErrs.categoryId !== undefined && (
                      <FormHelperText>{rowErrs.categoryId}</FormHelperText>
                    )}
                  </FormControl>
                </Stack>
              );
            })}
            <Button
              size="small"
              onClick={() =>
                setTargets((prev) => [
                  ...prev,
                  {
                    key: `n${prev.length + 1}`,
                    envelopeId: '',
                    mode: 'FIXED',
                    amount: '',
                    percent: '',
                    categoryId: '',
                  },
                ])
              }
            >
              {t('planned.addTarget')}
            </Button>
            {remainderCount === 1 && <FormHelperText>{t('planned.remainderHelp')}</FormHelperText>}
            <FormControl error={fieldErrors.fulfilment !== undefined}>
              <FormLabel>{t('planned.expected')}</FormLabel>
              <RadioGroup
                id="series-fulfilment"
                value={fulfilment}
                onChange={(_event, value) => setFulfilment(value as Fulfilment)}
              >
                <FormControlLabel
                  value="EXPECTED"
                  control={<Radio />}
                  label={t('planned.expected')}
                />
                <FormControlLabel
                  value="AUTO_POST"
                  control={<Radio />}
                  label={t('planned.autoPost')}
                  disabled={!owner}
                />
              </RadioGroup>
              {!owner && <FormHelperText>{t('planned.autoPostOwnerOnly')}</FormHelperText>}
              {fieldErrors.fulfilment !== undefined && (
                <FormHelperText>{fieldErrors.fulfilment}</FormHelperText>
              )}
            </FormControl>
            <Button onClick={() => setAdvanced((v) => !v)}>{t('planned.advanced')}</Button>
            <Collapse in={advanced}>
              <Stack spacing={1.5}>
                <TextField
                  id="series-payee-contains"
                  label={t('planned.payeeContains')}
                  value={payeeContains}
                  onChange={(event) => setPayeeContains(event.target.value)}
                  error={fieldErrors.payeeContains !== undefined}
                  helperText={fieldErrors.payeeContains}
                />
                <TextField
                  id="series-tolerance"
                  label={t('planned.amountTolerance')}
                  value={tolerance}
                  onChange={(event) => setTolerance(event.target.value)}
                  error={fieldErrors.amountToleranceBp !== undefined}
                  helperText={fieldErrors.amountToleranceBp}
                />
                <TextField
                  id="series-window"
                  label={t('planned.windowDays')}
                  value={windowDays}
                  onChange={(event) => setWindowDays(event.target.value)}
                  error={fieldErrors.windowDays !== undefined}
                  helperText={fieldErrors.windowDays}
                />
              </Stack>
            </Collapse>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={guard.requestClose}>{t('common.cancel')}</Button>
          <SubmitButton
            disabled={
              signed === null ||
              signed === 0 ||
              name.trim().length === 0 ||
              validateRule(rule) !== null ||
              planOver ||
              remainderCount > 1
            }
            loading={submitting}
            data-confirm-opener={SAVE_OPENER}
            onClick={() => void handleSubmit()}
          >
            {mode === 'create' ? t('planned.create') : t('planned.save')}
          </SubmitButton>
        </DialogActions>
      </GuardedDialog>
    </>
  );
}
